import React from 'react';

import {
	BUTTON_TYPES,
	DropdownOptionType,
	EllipsisIcon,
	EvDropdownMenu,
	EvIcon,
	EvTableColumn,
	IDropdownMenuOption,
	OptionType
} from '@evinced-private/ui-common';

import { formatDate } from '../../helpers/DateFormatHelper';
import { IPropertyViewRow } from '../../interfaces/PropertyViews';
import { DeleteViewPopup } from '../delete-view-popup/DeleteViewPopup';
import { EditViewButton } from '../edit-view-popup/EditViewButton';

interface IRenderCellPayload {
	issueTypesList: OptionType[];
	issueSeveritiesList: OptionType[];
}

const renderLastUpdatedCell = (cell: string, row: IPropertyViewRow): string => {
	return row.updatedTime ? formatDate(cell) : 'N/A';
};

const renderNameCell = (
	cell: string,
	row: IPropertyViewRow,
	payload: IRenderCellPayload
): JSX.Element => {
	const { issueTypesList, issueSeveritiesList } = payload;

	return (
		<EditViewButton
			propertyId={row.propertyId}
			view={row}
			type={BUTTON_TYPES.ICON}
			innerText={cell}
			issueTypesList={issueTypesList}
			issueSeveritiesList={issueSeveritiesList}
		/>
	);
};

const renderActionsCell = (
	cell: string,
	row: IPropertyViewRow,
	payload: IRenderCellPayload
): JSX.Element => {
	const { issueTypesList, issueSeveritiesList } = payload;
	const options: IDropdownMenuOption[] = [
		{
			title: 'Edit',
			id: 'edit-view',
			type: DropdownOptionType.CUSTOM,
			renderOption: () => (
				<EditViewButton
					propertyId={row.propertyId}
					view={row}
					type={BUTTON_TYPES.ICON}
					innerText="Edit"
					issueTypesList={issueTypesList}
					issueSeveritiesList={issueSeveritiesList}
				/>
			)
		},
		{
			title: 'Delete',
			id: 'delete-view',
			type: DropdownOptionType.CUSTOM,
			renderOption: (accessibilityProps: Record<string, string>) => (
				<DeleteViewPopup view={row} accessibilityProps={accessibilityProps} />
			)
		}
	];

	return (
		<div className="actions-cell">
			<EvDropdownMenu
				id={`menu-${row.id}`}
				options={options}
				triggerButtonProps={{
					type: BUTTON_TYPES.ICON,
					ariaHaspopup: 'menu',
					title: 'Property View Actions',
					children: (
						<div className="menu-trigger-icon">
							<EvIcon icon={EllipsisIcon} small />
						</div>
					)
				}}
			/>
		</div>
	);
};

export const getViewsTableColumns = (
	issueTypesList: OptionType[],
	issueSeveritiesList: OptionType[]
): EvTableColumn[] => {
	return [
		{
			dataField: 'name',
			text: 'Name',
			style: { width: '60%' },
			headerStyle: { width: '60%' },
			sort: true,
			formatter: (cell: string, row: IPropertyViewRow) =>
				renderNameCell(cell, row, { issueTypesList, issueSeveritiesList })
		},
		{
			dataField: 'updatedTime',
			text: 'Updated',
			style: { width: '30%' },
			headerStyle: { width: '30%' },
			sort: true,
			formatter: renderLastUpdatedCell
		},
		{
			dataField: 'actions',
			text: 'Actions',
			style: { width: '10%' },
			classes: 'actions-cell-td',
			headerStyle: { width: '10%' },
			sort: false,
			formatter: (cell: string, row: IPropertyViewRow) =>
				renderActionsCell(cell, row, { issueTypesList, issueSeveritiesList })
		}
	];
};

export const getFilterViews = (
	views: IPropertyViewRow[],
	searchFilter: string
): IPropertyViewRow[] => {
	return views.filter((property) => property.searchStr.includes(searchFilter.toLowerCase()));
};
